import { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  emergencyContacts as seedContacts,
  safetySnapshot,
  incidents as mockIncidents,
} from "@/data/mockData";
import { fetchIncidents, subscribeIncidents } from "@/data/incidentsRepo";
import { isSupabaseConfigured } from "@/lib/supabaseClient";

const SentinelContext = createContext(null);

export function SentinelProvider({ children }) {
  const [guardianMode, setGuardianMode] = useState(() => {
    try {
      const raw = localStorage.getItem("sentinel.guardian");
      return raw === null ? safetySnapshot.guardianMode : JSON.parse(raw);
    } catch {
      return safetySnapshot.guardianMode;
    }
  });
  const [contacts, setContacts] = useState(() => {
    try {
      const raw = localStorage.getItem("sentinel.contacts.v2");
      return raw ? JSON.parse(raw) : seedContacts;
    } catch {
      return seedContacts;
    }
  });

  useEffect(() => {
    localStorage.setItem("sentinel.guardian", JSON.stringify(guardianMode));
  }, [guardianMode]);
  useEffect(() => {
    localStorage.setItem("sentinel.contacts.v2", JSON.stringify(contacts));
  }, [contacts]);

  // Incident data — always live from Supabase.
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);

  const load = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setIncidents(mockIncidents);
      setLive(false);
      setLoading(false);
      return;
    }
    try {
      const rows = await fetchIncidents();
      setIncidents(rows || []);
      setLive(true);
      setError(null);
    } catch (e) {
      // Keep the last good data on a transient failure instead of showing demo data.
      setError(e?.message || "Unable to reach the Sentinel data service.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const unsubscribe = subscribeIncidents(load);
    const poll = setInterval(load, 15000); // resilience backup for realtime
    return () => {
      unsubscribe();
      clearInterval(poll);
    };
  }, [load]);

  const latestIncident = incidents[0] || null;
  const activeScore = latestIncident ? latestIncident.score : 0;
  const activeLevel = latestIncident ? latestIncident.level : "SAFE";
  const riskScore = activeScore;
  const safetyState = guardianMode ? activeLevel : "PAUSED";
  const monitoring = guardianMode;

  const getIncident = (id) => incidents.find((i) => i.id === id) || null;

  const addContact = (contact) => {
    setContacts((prev) => [
      ...prev,
      { ...contact, id: `c-${Date.now()}`, primary: false },
    ]);
  };

  const removeContact = (id) => {
    setContacts((prev) => prev.filter((c) => c.id !== id));
  };

  const setPrimary = (id) => {
    setContacts((prev) => prev.map((c) => ({ ...c, primary: c.id === id })));
  };

  return (
    <SentinelContext.Provider
      value={{
        guardianMode,
        setGuardianMode,
        monitoring,
        safetyState,
        riskScore,
        loading,
        error,
        refresh: load,
        incidents,
        latestIncident,
        getIncident,
        live,
        contacts,
        addContact,
        removeContact,
        setPrimary,
      }}
    >
      {children}
    </SentinelContext.Provider>
  );
}

export const useSentinel = () => useContext(SentinelContext);


